import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from "@angular/core";
import { AbstractControl, ControlContainer, FormArray, FormGroup } from "@angular/forms";
import { BehaviorSubject, Observable, Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { LgdFormControlFactory, setEnabled } from "@tylertech/lgd-reactive-forms";
import { Command, IconCommand } from "@tylertech/commands-module";
import { EntityAlert } from "@tylertech/tyler-api-types";
import { AlertCardInfo } from "../../interfaces/alertCardInfo";
import { EntityAlertCode } from "../../interfaces/entityAlertCode";
@Component({
    selector: "alert",
    templateUrl: "./alert.tmpl.html",
    styleUrls: ["./alert.component.scss"],
    standalone: false
})
export class AlertComponent implements OnInit, OnDestroy {
    private readonly destructor$ = new Subject<void>();
    private readonly alertCodesSubject = new BehaviorSubject<ReadonlyArray<EntityAlertCode>>([]);
    readonly alertForm: FormGroup;
    readonly deleteCommand: Command;
    private formIndex: number;
    @Input()
    formArrayName: string;

    @Input()
    model: AlertCardInfo;

    @Input()
    viewOnly: boolean;

    @Output("delete")
    readonly deleteEvent = new EventEmitter<EntityAlert>();

    constructor(private readonly controlContainer: ControlContainer) {
        const tomorrow = new Date(Date.now());
        tomorrow.setDate(tomorrow.getDate() + 1);
        tomorrow.setHours(0);
        tomorrow.setMinutes(0);
        tomorrow.setSeconds(0);
        this.alertForm = new FormGroup({
            code: LgdFormControlFactory.select<EntityAlertCode>(undefined, true, {
                displayValueFn: x => (x ? `${x.Code} - ${x.Description}` : ""),
            }),
            expirationDateAndTime: LgdFormControlFactory.date(undefined, false, {
                min: () => (this.codeControl?.value?.IsCustom ? tomorrow : undefined),
            }),
            // TODO - This could just be a label and value, why was it a control?
            createdBy: LgdFormControlFactory.text(undefined, false, {}),
            // TODO - This could just be a label and value, why was it a control?
            createdDate: LgdFormControlFactory.date(undefined, false, {}),
            message: LgdFormControlFactory.textarea(undefined, true, {
                maxLength: 50,
                rows: 5,
            }),
        });
        this.deleteCommand = new IconCommand({
            icon: "delete",
            label: null,
            action: () => this.onDelete(),
            getEnabled: () => !this.viewOnly,
        });
    }

    private get containingFormGroup(): FormGroup {
        const parentControl = this.controlContainer.control;
        if (!(parentControl instanceof FormGroup)) {
            throw new Error(`${AlertComponent.name} requires a containing FormGroup.`);
        }
        return parentControl;
    }

    private get alertsFormArray(): FormArray {
        const alertsFormArray = this.containingFormGroup?.get(this.formArrayName);
        if (!alertsFormArray) {
            throw new Error("Could not find the control that 'formArrayName' references.");
        }
        if (!(alertsFormArray instanceof FormArray)) {
            throw new Error("AbstractControl that 'formArrayName' references must be a FormArray.");
        }
        return alertsFormArray;
    }

    private get alert(): EntityAlert {
        return this.model.CardAlert;
    }

    get isExpired(): boolean {
        return !this.alert.ExpirationDateAndTime ? false : this.alert.ExpirationDateAndTime < new Date();
    }

    private get codeControl(): AbstractControl<EntityAlertCode> {
        return this.alertForm.get("code");
    }

    private get expirationDateAndTimeControl(): AbstractControl<Date> {
        return this.alertForm.get("expirationDateAndTime");
    }

    private get createdByControl(): AbstractControl<string> {
        return this.alertForm.get("createdBy");
    }

    private get createdDateControl(): AbstractControl<Date> {
        return this.alertForm.get("createdDate");
    }

    private get messageControl(): AbstractControl<string> {
        return this.alertForm.get("message");
    }

    private get alertCodes(): ReadonlyArray<EntityAlertCode> {
        return this.alertCodesSubject.value;
    }

    get alertCodesObservable(): Observable<ReadonlyArray<EntityAlertCode>> {
        return this.alertCodesSubject.asObservable();
    }

    ngOnDestroy(): void {
        this.alertsFormArray.removeAt(this.formIndex);
        this.destructor$.next();
        this.destructor$.complete();
    }

    ngOnInit(): void {
        if (!this.model) {
            throw "alert.component | Required binding missing: model";
        }
        const self = this;
        this.formIndex = this.alertsFormArray.length;
        this.alertsFormArray.push(this.alertForm);
        const unexpiredAlertCodes = this.model.CardAlertCodes.filter(a => {
            return a.IsCustom || this.isExpiredAlert(a) || this.alert.AlertId === a.Id;
        });
        this.alertCodesSubject.next(unexpiredAlertCodes);
        const initialAlertCode = this.alertCodes.find(a => a.Id === this.alert.AlertId);
        if (this.alert.CreatedDate) {
            this.alert.CreatedDate = new Date(this.alert.CreatedDate.toString());
        }
        if (this.alert.ExpirationDateAndTime) {
            this.alert.ExpirationDateAndTime = new Date(this.alert.ExpirationDateAndTime.toString());
        }
        setEnabled(this.codeControl, !this.viewOnly).setValue(this.alert.AlertId);
        subscribeToValueChanges(this.codeControl, (newValue: EntityAlertCode) => {
            this.onAlertCodeChange(initialAlertCode);
            this.alert.AlertId = newValue.Id;
        });
        setEnabled(this.expirationDateAndTimeControl, !this.viewOnly && initialAlertCode.IsCustom).setValue(
            this.alert.ExpirationDateAndTime
        );
        subscribeToValueChanges(this.expirationDateAndTimeControl, (newValue: Date) => {
            /* "mask-string=\"99/99/9999\"" */
            this.alert.ExpirationDateAndTime = newValue;
        });
        setEnabled(this.createdByControl, false).setValue(this.alert.CreatedBy);
        subscribeToValueChanges(this.createdByControl, (newValue: string) => {
            this.alert.CreatedBy = newValue;
        });
        setEnabled(this.createdDateControl, false).setValue(this.alert.CreatedDate);
        subscribeToValueChanges(this.createdDateControl, (newValue: Date) => {
            this.alert.CreatedDate = newValue;
        });
        setEnabled(this.messageControl, !this.viewOnly).setValue(this.alert.Message);
        subscribeToValueChanges(this.messageControl, (newValue: string) => {
            this.alert.Message = newValue;
        });
        function subscribeToValueChanges<T = unknown>(
            control: AbstractControl<T>,
            subscriptionFn: (newValue: T) => void
        ): void {
            control.valueChanges.pipe(takeUntil(self.destructor$)).subscribe(x => {
                subscriptionFn(x);
            });
        }
    }

    private onAlertCodeChange(alertCode: EntityAlertCode): void {
        setEnabled(this.expirationDateAndTimeControl, !this.viewOnly && alertCode.IsCustom);
        if (alertCode.IsCustom) {
            return;
        }
        if (alertCode.ExpirationTime) {
            const createdDate = this.alert.CreatedDate.getDate();
            const expirationDate = new Date();
            expirationDate.setDate(createdDate + alertCode.ExpirationTime);
            this.alert.ExpirationDateAndTime = expirationDate;
        } else {
            this.alert.ExpirationDateAndTime = alertCode.ExpirationDate;
        }
    }

    private onDelete(): void {
        this.deleteEvent.emit(this.alert);
    }

    private isExpiredAlert(a: EntityAlertCode): boolean {
        return !a.ExpirationDate ? true : new Date(a.ExpirationDate.toString()) <= new Date();
    }
}
